import { Component, Input, OnInit, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DayOfWeek } from '../../../api/gen';

/**
 * Value emitted by the access-window field. Times are "HH:mm:ss" (or null = no time window);
 * days is the list of allowed weekdays (null/empty = every day). Shape matches the backend
 * pool/space window fields exactly so callers can spread it straight into the request.
 */
export interface AccessWindowValue {
    start: string | null;
    end: string | null;
    days: DayOfWeek[] | null;
}

/**
 * Reusable daily access-window editor: an enable toggle, From/To time pickers and weekday chips.
 * Two-way bound via [(value)]. When disabled it emits an all-null value (= no window / 24-7).
 * Used by the pool-access window (pool config) and the space restricted-access window.
 */
@Component({
    selector: 'app-access-window-field',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './access-window-field.component.html'
})
export class AccessWindowFieldComponent implements OnInit {
    /** Two-way bound window value. */
    value = model<AccessWindowValue>({ start: null, end: null, days: null });

    @Input() title = 'Daily access window';
    @Input() description = '';
    @Input() enabledLabel = 'Restrict to a daily time window';
    @Input() defaultStart = '06:00';
    @Input() defaultEnd = '17:00';

    readonly days: { key: DayOfWeek; label: string }[] = [
        { key: DayOfWeek.Monday, label: 'Mon' },
        { key: DayOfWeek.Tuesday, label: 'Tue' },
        { key: DayOfWeek.Wednesday, label: 'Wed' },
        { key: DayOfWeek.Thursday, label: 'Thu' },
        { key: DayOfWeek.Friday, label: 'Fri' },
        { key: DayOfWeek.Saturday, label: 'Sat' },
        { key: DayOfWeek.Sunday, label: 'Sun' }
    ];

    enabled = signal(false);
    start = signal('');   // "HH:mm" for the <input type="time">
    end = signal('');
    selectedDays = signal<Set<DayOfWeek>>(new Set<DayOfWeek>());

    ngOnInit(): void {
        const v = this.value();
        const hasWindow = !!(v.start && v.end);
        this.enabled.set(hasWindow);
        this.start.set(hasWindow ? this.toHHmm(v.start!) : this.defaultStart);
        this.end.set(hasWindow ? this.toHHmm(v.end!) : this.defaultEnd);
        this.selectedDays.set(new Set(v.days ?? []));
    }

    /** True unless the window is enabled but missing a bound — lets the parent block submit. */
    get isValid(): boolean {
        return !this.enabled() || (!!this.start() && !!this.end());
    }

    toggleEnabled(on: boolean): void {
        this.enabled.set(on);
        if (on) {
            if (!this.start()) this.start.set(this.defaultStart);
            if (!this.end()) this.end.set(this.defaultEnd);
        }
        this.emit();
    }

    setStart(v: string): void { this.start.set(v); this.emit(); }
    setEnd(v: string): void { this.end.set(v); this.emit(); }

    toggleDay(d: DayOfWeek): void {
        const next = new Set(this.selectedDays());
        if (next.has(d)) next.delete(d); else next.add(d);
        this.selectedDays.set(next);
        this.emit();
    }

    isDaySelected(d: DayOfWeek): boolean { return this.selectedDays().has(d); }

    /** Lexicographic compare is fine for fixed "HH:mm" — used only for the overnight hint. */
    get isOvernight(): boolean {
        return !!(this.start() && this.end()) && this.end() <= this.start();
    }

    private emit(): void {
        if (!this.enabled()) {
            this.value.set({ start: null, end: null, days: null });
            return;
        }
        // Preserve weekday order (Mon→Sun) for a stable, readable payload.
        const days = this.days.map(d => d.key).filter(k => this.selectedDays().has(k));
        this.value.set({
            start: this.start() ? this.toApi(this.start()) : null,
            end: this.end() ? this.toApi(this.end()) : null,
            days: days.length ? days : null
        });
    }

    private toHHmm(t: string): string { return t.length >= 5 ? t.substring(0, 5) : t; }
    private toApi(t: string): string { return t.length === 5 ? `${t}:00` : t; }
}
